import React, { useState, useEffect } from 'react';
import { 
    collection, 
    getDocs, 
    query, 
    doc, 
    updateDoc, 
    deleteDoc, 
    collectionGroup 
} from 'firebase/firestore';
import { fireDB as db, appId } from '../../services/firebase'; 
import { ArrowLeft, User, AlertCircle, CheckCircle2, RefreshCcw, Trash2, Bug, MessageSquare, Lightbulb, HelpCircle } from 'lucide-react';

export default function AdminView({ user, setView }) {
    const [users, setUsers] = useState([]);
    const [reports, setReports] = useState([]);
    const [activeTab, setActiveTab] = useState('users');
    const [reportFilter, setReportFilter] = useState('all'); // 'all', 'typo', 'suggest', 'etc'
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        setLoading(true);
        try {
            // 1. 회원 목록 가져오기
            const profileQuery = query(collectionGroup(db, 'profile')); 
            const userSnapshot = await getDocs(profileQuery);
            const userList = [];
            userSnapshot.forEach((docSnap) => {
                if (docSnap.id === 'info') {
                    const parentPath = docSnap.ref.parent.parent; 
                    const uid = parentPath ? parentPath.id : 'unknown';
                    userList.push({ uid, ...docSnap.data() });
                }
            });
            userList.sort((a, b) => (b.lastLogin?.seconds || 0) - (a.lastLogin?.seconds || 0));
            setUsers(userList);

            // 2. 신고/건의 내역 가져오기
            const reportsRef = collection(db, 'artifacts', appId, 'reports');
            const reportSnapshot = await getDocs(reportsRef);
            const reportList = reportSnapshot.docs.map(doc => {
                const data = doc.data();
                return { id: doc.id, ...data, sortDate: data.createdAt?.seconds || 0 };
            });
            reportList.sort((a, b) => b.sortDate - a.sortDate);
            setReports(reportList);

        } catch (error) {
            console.error("Admin Data Fetch Error:", error);
            alert("데이터 로딩 실패: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleDeleteUser = async (userName, targetUid) => {
        if (!targetUid) return alert("오류: 사용자 UID가 없습니다.");
        if (!window.confirm(`⚠️ 경고: [${userName}] 님의 데이터를 삭제하시겠습니까?`)) return;
        try {
            const userDocPath = `artifacts/${appId}/users/${targetUid}/profile/info`;
            await deleteDoc(doc(db, userDocPath));
            setUsers(prev => prev.filter(u => u.uid !== targetUid));
            alert("성공적으로 삭제되었습니다.");
        } catch (e) {
            alert(`삭제 실패: ${e.message}`);
        }
    };

    const toggleReportStatus = async (reportId, currentStatus) => {
        const newStatus = currentStatus === 'resolved' ? 'pending' : 'resolved';
        setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: newStatus } : r));
        try {
            await updateDoc(doc(db, 'artifacts', appId, 'reports', reportId), { status: newStatus });
        } catch (e) {
            alert("상태 변경 실패");
        }
    };

    // --- 유형별 아이콘 및 텍스트 헬퍼 ---
    const getReportTypeInfo = (type) => {
        switch(type) {
            case 'typo': return { label: '오타 제보', color: 'text-rose-400 bg-rose-950/30 border-rose-500/30', icon: <Bug className="w-3 h-3"/> };
            case 'suggest': return { label: '개선 건의', color: 'text-emerald-400 bg-emerald-950/30 border-emerald-500/30', icon: <Lightbulb className="w-3 h-3"/> };
            default: return { label: '기타 문의', color: 'text-sky-400 bg-sky-950/30 border-sky-500/30', icon: <HelpCircle className="w-3 h-3"/> };
        }
    };

    // 필터링된 신고 목록
    const filteredReports = reports.filter(r => {
        if (reportFilter === 'all') return true;
        return r.type === reportFilter;
    });

    if (loading) return <div className="text-center py-20 text-slate-400">데이터 로딩 중...</div>;

    return (
        <div className="animate-fadeIn pb-20">
            {/* 상단 헤더 및 통계 생략 (기존과 동일) */}
            <div className="flex items-center justify-between mb-4 bg-slate-800 p-3 rounded-xl shadow-lg border border-slate-700">
                <h2 className="text-lg font-bold text-white flex items-center">
                    <User className="w-5 h-5 mr-2 text-indigo-400"/> 관리자 페이지
                </h2>
                <div className="flex items-center gap-2">
                    <button onClick={fetchData} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition"><RefreshCcw className="w-4 h-4"/></button>
                    <button onClick={() => setView('home')} className="flex items-center text-xs text-slate-400 hover:text-white transition px-3 py-1.5 border border-slate-600 rounded-lg"><ArrowLeft className="w-3 h-3 mr-1"/> 나가기</button>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-slate-800 p-3 rounded-xl border border-slate-700 shadow-md flex justify-between items-center">
                    <span className="text-slate-400 text-xs font-bold">총 가입자</span>
                    <span className="text-xl font-black text-indigo-400">{users.length}명</span>
                </div>
                <div className="bg-slate-800 p-3 rounded-xl border border-slate-700 shadow-md flex justify-between items-center">
                    <span className="text-slate-400 text-xs font-bold">미처리 신고</span>
                    <span className="text-xl font-black text-amber-500">{reports.filter(r => r.status !== 'resolved').length}건</span>
                </div>
            </div>

            {/* 메인 탭 */}
            <div className="flex mb-3 bg-slate-800 p-1 rounded-lg border border-slate-700">
                <button onClick={() => setActiveTab('users')} className={`flex-1 py-2 text-xs font-bold rounded-md transition-all ${activeTab === 'users' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}>회원 목록</button>
                <button onClick={() => setActiveTab('reports')} className={`flex-1 py-2 text-xs font-bold rounded-md transition-all ${activeTab === 'reports' ? 'bg-amber-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}>신고/건의</button>
            </div>

            /* 탭 1: 회원 목록 (이름+전화번호 통합 및 로그인 상세화) */
            {activeTab === 'users' && (
                <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-lg">
                    <table className="w-full text-left text-[11px] text-slate-300">
                        <thead className="bg-slate-900/50 text-slate-500 font-bold border-b border-slate-700">
                            <tr>
                                <th className="p-2 pl-4">회원 정보 (이름/번호/ID)</th>
                                <th className="p-2 text-center">로그인 기록 (횟수/최근)</th>
                                <th className="p-2 text-right pr-4">가입일 / 관리</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                            {users.map((u, i) => {
                                // 안전한 날짜 변환 함수 (2자리 연도 형식)
                                const getFormattedDate = (dateObj) => {
                                    try {
                                        if (!dateObj) return '-';
                                        const date = dateObj.seconds ? new Date(dateObj.seconds * 1000) : new Date(dateObj);
                                        if (isNaN(date.getTime())) return '-';
                                        return date.toLocaleDateString('ko-KR', {
                                            year: '2-digit', month: '2-digit', day: '2-digit'
                                        });
                                    } catch (e) { return '-'; }
                                };

                                return (
                                    <tr key={u.uid || i} className="hover:bg-slate-700/20 transition-colors">
                                        {/* 1. 이름 + 전화번호 + 아이디 통합 영역 */}
                                        <td className="p-2 pl-4">
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-slate-200">{u.name || '익명'}</span>
                                                <span className="text-indigo-400 font-mono text-[10px]">{u.phone || '-'}</span>
                                            </div>
                                            <div className="text-[9px] text-slate-500 truncate max-w-[150px]">{u.email}</div>
                                        </td>

                                        {/* 2. 로그인 기록 (횟수 및 최근 날짜) */}
                                        <td className="p-2 text-center">
                                            <div className="flex flex-col items-center">
                                                <span className="text-emerald-400 font-bold">{u.loginCount || 1}회</span>
                                                <span className="text-[9px] text-slate-500 font-mono">
                                                    {getFormattedDate(u.lastLogin)}
                                                </span>
                                            </div>
                                        </td>

                                        {/* 3. 가입일 및 관리 버튼 */}
                                        <td className="p-2 text-right pr-4">
                                            <div className="flex flex-col items-end">
                                                <span className="text-slate-500 font-mono text-[10px]">
                                                    {getFormattedDate(u.createdAt)}
                                                </span>
                                                <button 
                                                    onClick={() => handleDeleteUser(u.name, u.uid)}
                                                    className="text-[9px] text-rose-500/40 hover:text-rose-400"
                                                >
                                                    [탈퇴]
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* 탭 2: 신고 내역 */}
            {activeTab === 'reports' && (
                <div className="animate-fadeIn">
                    {/* 서브 필터 탭 */}
                    <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
                        {[
                            { id: 'all', label: '전체', icon: <MessageSquare className="w-3 h-3"/> },
                            { id: 'typo', label: '오타 제보', icon: <Bug className="w-3 h-3"/> },
                            { id: 'suggest', label: '개선 건의', icon: <Lightbulb className="w-3 h-3"/> },
                            { id: 'etc', label: '기타', icon: <HelpCircle className="w-3 h-3"/> }
                        ].map(f => (
                            <button
                                key={`filter-btn-${f.id}`} // 고유 키 부여
                                onClick={() => setReportFilter(f.id)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border transition-all whitespace-nowrap
                                    ${reportFilter === f.id ? 'bg-amber-600 border-amber-500 text-white shadow-lg' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'}`}
                            >
                                {f.icon} {f.label}
                            </button>
                        ))}
                    </div>

                    <div className="space-y-2">
                      {filteredReports.length === 0 ? (
                        <div key="empty-msg" className="text-center py-10 text-slate-500 text-xs border border-dashed border-slate-700 rounded-xl">
                          해당 유형의 내역이 없습니다.
                        </div>
                      ) : (
                        filteredReports.map((item) => {
                          const currentType = item.type || 'etc'; 
                          const typeInfo = getReportTypeInfo(currentType);
                          const displayContent = item.userText || item.content || "(내용 없음)";
                          const isResolved = item.status === 'resolved';

                          return (
                            <div 
                              key={item.id} 
                              className={`p-3 rounded-lg border transition-all ${
                                isResolved ? 'bg-slate-800/40 border-slate-700 opacity-60' : 'bg-slate-800 border-slate-700 shadow-sm'
                              }`}
                            >
                              {/* 첫 줄: 유형 태그 | 시간 | 제보자 (한 줄로 통합) */}
                              <div className="flex items-center justify-between mb-2 text-[10px]">
                                <div className="flex items-center gap-2">
                                  <span className={`px-1.5 py-0.5 rounded font-bold border ${typeInfo.color}`}>
                                    {typeInfo.label}
                                  </span>
                                  <span className="text-slate-500 font-mono">
                                    {item.createdAt?.seconds ? new Date(item.createdAt.seconds * 1000).toLocaleString() : ''}
                                  </span>
                                  <span className="text-indigo-400/70">
                                    | {item.userEmail || "익명"}
                                  </span>
                                </div>
                                
                                {/* 처리 상태 및 변경 버튼 (작게 표시) */}
                                <div className="flex items-center gap-2">
                                  <span className={isResolved ? 'text-green-500' : 'text-amber-500'}>
                                    {isResolved ? '완료' : '미처리'}
                                  </span>
                                  <button 
                                    onClick={() => toggleReportStatus(item.id, item.status)}
                                    className="text-slate-600 hover:text-slate-300 underline decoration-slate-700"
                                  >
                                    변경
                                  </button>
                                </div>
                              </div>
                              
                              {/* 제보 내용 (여백 최적화) */}
                              <div className="bg-slate-900/30 p-2.5 rounded border border-slate-700/30">
                                <p className="text-white text-xs whitespace-pre-wrap leading-normal">
                                  {displayContent}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                </div>
            )}
        </div>
    );
}